import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContent";

// Cinematic Dubai hero (Pexels public hotlink)
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/4625747/4625747-uhd_3840_2160_30fps.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2400&q=85";

export default function Hero() {
  const content = useSiteContent();
  const cfg = content?.hero || {
    headline_line_1: "Building Legacies.",
    headline_line_2: "Creating Landmarks.",
    subheadline: "Residences for the few who require everything.",
    cta_label: "View the Collection",
  };
  const wrapRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const h = window.innerHeight;
      const total = rect.height - h;
      const p = Math.min(1, Math.max(0, -rect.top / total));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scale = 1 - progress * 0.06;
  const opacity = 1 - progress * 0.55;
  const titleY = -progress * 60;
  const titleOpacity = 1 - progress * 0.85;

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={wrapRef}
      className="relative w-full"
      style={{ height: "180vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 will-change-transform"
          style={{ transform: `scale(${scale})`, opacity }}
        >
          <video
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/40" />
          {/* fade into the cream page background */}
          <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-[#f5f0e8] via-[#f5f0e8]/60 to-transparent" />
        </div>

        <div
          className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-center pt-32 lg:pt-40 pb-24"
          style={{ transform: `translateY(${titleY}px)`, opacity: titleOpacity }}
        >
          <h1
            data-testid="hero-headline"
            className="font-display font-light text-white leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-[108px] max-w-5xl"
          >
            {cfg.headline_line_1}<br />
            {cfg.headline_line_2}
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-10 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl text-white/85 leading-snug font-light"
          >
            {cfg.subheadline}
          </p>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
            <button
              data-testid="hero-cta-view-collection"
              onClick={() =>
                document.getElementById("listings")?.scrollIntoView({ behavior: "smooth" })
              }
              className="self-start px-7 py-4 bg-[#c5a059] text-[#1c1c1c] uppercase text-[11px] tracking-[0.32em] hover:bg-white transition-colors duration-500"
            >
              {cfg.cta_label}
            </button>
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <ArrowDown size={14} className="text-[#c5a059]" />
              <span className="uppercase tracking-[0.28em] text-[11px]">Scroll to begin</span>
            </div>
          </div>
        </div>

        {/* Side meta — text first, line after */}
        <div className="hidden lg:flex absolute right-12 top-1/2 -translate-y-1/2 z-10 flex-col gap-7 text-[10px] uppercase tracking-[0.32em] text-white/60">
          <div className="flex items-center gap-3 justify-end">
            01 · Burj Khalifa <span className="w-8 h-px bg-[#c5a059]" />
          </div>
          <div className="flex items-center gap-3 justify-end">
            02 · Palm Jumeirah <span className="w-8 h-px bg-white/20" />
          </div>
          <div className="flex items-center gap-3 justify-end">
            03 · Museum of the Future <span className="w-8 h-px bg-white/20" />
          </div>
          <div className="flex items-center gap-3 justify-end">
            04 · Burj Al Arab <span className="w-8 h-px bg-white/20" />
          </div>
        </div>
      </div>
    </section>
  );
}
